'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

type AuthMode = 'login' | 'signup';
type UserType = 'attendee' | 'organizer';
type Step = 'credentials' | 'otp' | 'success';

interface FormData {
  name: string;
  phone: string;
  email: string;
  password: string;
  userType: UserType;
}

export default function LoginPage() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [step, setStep] = useState<Step>('credentials');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    password: '',
    userType: 'attendee'
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const validatePhone = (phone: string) => {
    return /^[+]?[\d\s\-()]{10,15}$/.test(phone.replace(/\s/g, ''));
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.phone) {
      setError('Please enter your phone number.');
      return;
    }
    if (!validatePhone(formData.phone)) {
      setError('Please enter a valid phone number.');
      return;
    }
    if (mode === 'signup' && !formData.name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (mode === 'signup' && !formData.email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      // Simulate Twilio SMS OTP send
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setStep('otp');
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
    } catch {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    pasted.split('').forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);
    if (pasted.length > 0) {
      otpRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length < 6) {
      setError('Please enter the complete 6-digit OTP.');
      return;
    }

    setLoading(true);
    try {
      // Simulate Twilio OTP verification
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // For demo: accept any 6-digit OTP
      setStep('success');
    } catch {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
      setError('');
    } catch {
      setError('Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setStep('credentials');
    setError('');
    setOtp(['', '', '', '', '', '']);
    setFormData({ name: '', phone: '', email: '', password: '', userType: 'attendee' });
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ fontFamily: 'Roboto, sans-serif', background: '#FAF7F2' }}>
      
      {/* Left Panel - Branding */}
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden"
        style={{ background: '#1A0A00' }}>
        
        {/* Gold top accent */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(to right, #8B6914, #C9A84C, #E8C97A, #C9A84C, #8B6914)', zIndex: 20 }} />

        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://img.rocket.new/generatedImages/rocket_gen_img_15205edf8-1782154762213.png"
            alt="Vibrant music festival with colorful stage lights and enthusiastic crowd"
            className="w-full h-full object-cover opacity-20" />
          
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(26,10,0,0.97) 0%, rgba(26,10,0,0.75) 100%)' }} />
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)' }}>
              
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6" stroke="#FAF7F2" strokeWidth="2" />
                <circle cx="8" cy="8" r="2.5" fill="#FAF7F2" />
              </svg>
            </div>
            <span className="font-bold text-xl" style={{ letterSpacing: '0.3px', color: '#C9A84C', fontFamily: '"Playfair Display", Georgia, serif' }}>Rani Kamlapati</span>
          </Link>
        </div>

        {/* Center Content */}
        <div className="relative z-10">
          <div
            className="inline-flex items-center px-3 py-1 rounded mb-6"
            style={{ background: 'rgba(201,168,76,0.15)', borderRadius: '4px', border: '1px solid rgba(201,168,76,0.4)' }}>
            
            <span className="text-xs font-bold" style={{ letterSpacing: '1.5px', color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>ALL-IN-ONE EVENT MARKETPLACE</span>
          </div>
          <h1 className="text-4xl font-bold mb-4 leading-tight" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#E8C97A' }}>
            Your next great<br />experience starts here.
          </h1>
          <p className="text-lg mb-8" style={{ color: 'rgba(250,247,242,0.65)', fontFamily: 'Roboto, sans-serif' }}>
            Discover thousands of events. Buy tickets. Connect with organizers. All in one place.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6">
            {[
            { value: '50K+', label: 'Events Listed' },
            { value: '2M+', label: 'Happy Attendees' },
            { value: '10K+', label: 'Organizers' }].
            map((stat) =>
            <div key={stat.label}>
                <div className="text-2xl font-bold" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#C9A84C' }}>{stat.value}</div>
                <div className="text-sm" style={{ color: 'rgba(250,247,242,0.5)', fontFamily: 'Roboto, sans-serif' }}>{stat.label}</div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Testimonial */}
        <div className="relative z-10">
          <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.08)', borderRadius: '12px', border: '1px solid rgba(201,168,76,0.2)' }}>
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1d4551b80-1765511200828.png"
              alt="Sarah Johnson, event organizer testimonial"
              className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
            
            <div>
              <p className="text-sm italic mb-1" style={{ color: 'rgba(232,201,122,0.8)', fontFamily: '"Playfair Display", Georgia, serif' }}>
                &ldquo;Rani Kamlapati helped us sell out our 5,000-seat venue in under 48 hours. The platform is incredible.&rdquo;
              </p>
              <p className="text-xs" style={{ color: 'rgba(250,247,242,0.45)', fontFamily: 'Roboto, sans-serif' }}>Sarah Johnson &mdash; Founder, SoundWave Events</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link href="/" className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: '#FFE100' }}>
                
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5" stroke="#262629" strokeWidth="2" />
                  <circle cx="7" cy="7" r="2" fill="#262629" />
                </svg>
              </div>
              <span className="font-bold text-lg text-[#262629]">Rani Kamlapati</span>
            </Link>
          </div>

          {/* Success State */}
          {step === 'success' ?
          <div className="text-center animate-fadeInUp">
              <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: '#F0FDF4' }}>
              
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="14" fill="#22C55E" />
                  <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-[#262629] mb-2">
                {mode === 'login' ? 'Welcome back!' : 'Account created!'}
              </h2>
              <p className="text-[#71717A] mb-8">
                {mode === 'login' ? 'You\'ve successfully signed in to Rani Kamlapati.' :
              `Your ${formData.userType} account has been created successfully.`
              }
              </p>
              <Link
              href="/"
              className="flex items-center justify-center w-full h-12 rounded-lg font-medium text-base transition-all duration-200 hover:opacity-90"
              style={{
                background: '#262629',
                color: '#FFFFFF',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 500
              }}>
              
                {mode === 'login' ? 'Go to Events' : 'Start Exploring'}
              </Link>
            </div> :
          step === 'otp' ? (
          /* OTP Verification Step */
          <div className="animate-fadeInUp">
              <button
              onClick={() => setStep('credentials')}
              className="flex items-center gap-2 text-sm text-[#71717A] hover:text-[#262629] mb-6 transition-colors">
              
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
              </button>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[#262629] mb-2">Verify your number</h2>
                <p className="text-[#71717A] text-sm">
                  We sent a 6-digit code to <span className="font-medium text-[#262629]">{formData.phone}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyOTP}>
                {/* OTP Input */}
                <div className="flex gap-3 justify-center mb-6" onPaste={handleOtpPaste}>
                  {otp.map((digit, index) =>
                <input
                  key={index}
                  ref={(el) => {otpRefs.current[index] = el;}}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="w-12 h-14 text-center text-xl font-bold border-2 rounded-xl outline-none transition-all duration-200 focus:border-[#262629]"
                  style={{
                    borderColor: digit ? '#262629' : '#EDEEF2',
                    borderRadius: '12px',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#262629',
                    background: digit ? '#F9FAFB' : '#FFFFFF'
                  }} />

                )}
                </div>

                {error &&
              <div className="flex items-center gap-2 p-3 rounded-lg mb-4" style={{ background: '#FEF2F2', borderRadius: '8px' }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6" stroke="#EF4444" strokeWidth="1.2" />
                      <path d="M7 4V7" stroke="#EF4444" strokeWidth="1.2" strokeLinecap="round" />
                      <circle cx="7" cy="10" r="0.5" fill="#EF4444" />
                    </svg>
                    <span className="text-xs text-red-600">{error}</span>
                  </div>
              }

                <button
                type="submit"
                disabled={loading || otp.join('').length < 6}
                className="w-full h-12 rounded-lg font-medium text-base transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                style={{
                  background: '#262629',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 500
                }}>
                
                  {loading ?
                <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" strokeDasharray="20" strokeDashoffset="10" />
                      </svg>
                      Verifying...
                    </span> :
                'Verify & Continue'}
                </button>

                <div className="text-center">
                  <span className="text-sm text-[#71717A]">
                    Didn&apos;t receive the code?{' '}
                  </span>
                  <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={countdown > 0 || loading}
                  className="text-sm font-medium transition-colors"
                  style={{ color: countdown > 0 ? '#71717A' : '#262629' }}>
                  
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>
              </form>

              {/* Twilio Notice */}
              <div className="mt-6 p-3 rounded-lg flex items-start gap-2" style={{ background: '#F8F9FC', borderRadius: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0 mt-0.5">
                  <circle cx="7" cy="7" r="6" stroke="#71717A" strokeWidth="1.2" />
                  <path d="M7 6V10" stroke="#71717A" strokeWidth="1.2" strokeLinecap="round" />
                  <circle cx="7" cy="4" r="0.5" fill="#71717A" />
                </svg>
                <p className="text-xs text-[#71717A]">
                  SMS verification powered by <span className="font-medium">Twilio</span>. Standard message rates may apply.
                </p>
              </div>
            </div>) : (

          /* Credentials Step */
          <div className="animate-fadeInUp">
              {/* Mode Toggle */}
              <div className="flex items-center gap-1 p-1 rounded-xl mb-8" style={{ background: '#EDEEF2', borderRadius: '12px' }}>
                {(['login', 'signup'] as AuthMode[]).map((m) =>
              <button
                key={m}
                onClick={() => switchMode(m)}
                className="flex-1 h-10 rounded-lg font-medium text-sm transition-all duration-200"
                style={{
                  background: mode === m ? '#FFFFFF' : 'transparent',
                  color: mode === m ? '#262629' : '#71717A',
                  borderRadius: '10px',
                  fontWeight: mode === m ? 600 : 400,
                  boxShadow: mode === m ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}>
                
                    {m === 'login' ? 'Sign In' : 'Create Account'}
                  </button>
              )}
              </div>

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#262629] mb-1">
                  {mode === 'login' ? 'Welcome back' : 'Join Rani Kamlapati'}
                </h2>
                <p className="text-[#71717A] text-sm">
                  {mode === 'login' ? 'Sign in to access your tickets and saved events' : 'Create your account to start discovering events'
                }
                </p>
              </div>

              {/* User Type Selector (Signup only) */}
              {mode === 'signup' &&
            <div className="mb-5">
                  <label className="block text-xs font-medium text-[#71717A] mb-2 uppercase tracking-wide">I am a...</label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['attendee', 'organizer'] as UserType[]).map((type) =>
                <button
                  key={type}
                  type="button"
                  onClick={() => handleInputChange('userType', type)}
                  className="flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200"
                  style={{
                    borderColor: formData.userType === type ? '#262629' : '#EDEEF2',
                    background: formData.userType === type ? '#F9FAFB' : '#FFFFFF',
                    borderRadius: '12px'
                  }}>
                  
                        <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: formData.userType === type ? '#262629' : '#EDEEF2' }}>
                    
                          {type === 'attendee' ?
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <circle cx="7" cy="5" r="3" stroke={formData.userType === type ? 'white' : '#262629'} strokeWidth="1.2" />
                              <path d="M1 13C1 10.24 3.69 8 7 8C10.31 8 13 10.24 13 13" stroke={formData.userType === type ? 'white' : '#262629'} strokeWidth="1.2" strokeLinecap="round" />
                            </svg> :

                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <rect x="1" y="2" width="12" height="10" rx="1.5" stroke={formData.userType === type ? 'white' : '#262629'} strokeWidth="1.2" />
                              <path d="M1 5H13" stroke={formData.userType === type ? 'white' : '#262629'} strokeWidth="1.2" />
                              <path d="M4 1V3" stroke={formData.userType === type ? 'white' : '#262629'} strokeWidth="1.2" strokeLinecap="round" />
                              <path d="M10 1V3" stroke={formData.userType === type ? 'white' : '#262629'} strokeWidth="1.2" strokeLinecap="round" />
                            </svg>
                    }
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-[#262629] capitalize">{type}</p>
                          <p className="text-xs text-[#71717A]">
                            {type === 'attendee' ? 'Browse & buy tickets' : 'Create & manage events'}
                          </p>
                        </div>
                      </button>
                )}
                  </div>
                </div>
            }

              <form onSubmit={handleSendOTP} className="space-y-4">
                {/* Name (Signup only) */}
                {mode === 'signup' &&
              <div>
                    <label className="block text-xs font-medium text-[#71717A] mb-1.5 uppercase tracking-wide">Full Name</label>
                    <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="John Doe"
                  className="w-full h-11 px-4 border rounded-xl text-sm outline-none transition-all duration-200 focus:border-[#262629]"
                  style={{
                    borderColor: '#EDEEF2',
                    borderRadius: '12px',
                    color: '#262629',
                    background: '#FFFFFF',
                    fontSize: '14px'
                  }} />
                
                  </div>
              }

                {/* Email (Signup only) */}
                {mode === 'signup' &&
              <div>
                    <label className="block text-xs font-medium text-[#71717A] mb-1.5 uppercase tracking-wide">Email Address</label>
                    <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="john@example.com"
                  className="w-full h-11 px-4 border rounded-xl text-sm outline-none transition-all duration-200 focus:border-[#262629]"
                  style={{
                    borderColor: '#EDEEF2',
                    borderRadius: '12px',
                    color: '#262629',
                    background: '#FFFFFF',
                    fontSize: '14px'
                  }} />
                
                  </div>
              }

                {/* Phone Number */}
                <div>
                  <label className="block text-xs font-medium text-[#71717A] mb-1.5 uppercase tracking-wide">Phone Number</label>
                  <div className="relative">
                    <div
                    className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pr-3 border-r"
                    style={{ borderColor: '#EDEEF2' }}>
                    
                      <span className="text-sm">🇺🇸</span>
                      <span className="text-sm text-[#71717A]">+1</span>
                    </div>
                    <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="(555) 000-0000"
                    className="w-full h-11 pl-20 pr-4 border rounded-xl text-sm outline-none transition-all duration-200 focus:border-[#262629]"
                    style={{
                      borderColor: '#EDEEF2',
                      borderRadius: '12px',
                      color: '#262629',
                      background: '#FFFFFF',
                      fontSize: '14px'
                    }} />
                  
                  </div>
                  <p className="text-xs text-[#71717A] mt-1">We&apos;ll send a verification code via SMS</p>
                </div>

                {/* Password (Login only) */}
                {mode === 'login' &&
              <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-[#71717A] uppercase tracking-wide">Password</label>
                      <Link href="#" className="text-xs text-[#262629] hover:underline">Forgot password?</Link>
                    </div>
                    <div className="relative">
                      <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter your password"
                    className="w-full h-11 px-4 pr-10 border rounded-xl text-sm outline-none transition-all duration-200 focus:border-[#262629]"
                    style={{
                      borderColor: '#EDEEF2',
                      borderRadius: '12px',
                      color: '#262629',
                      background: '#FFFFFF',
                      fontSize: '14px'
                    }} />
                  
                      <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#262629] transition-colors">
                    
                        {showPassword ?
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2L14 14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                            <path d="M6.5 6.5C6.18 6.82 6 7.24 6 7.7C6 8.97 7.03 10 8.3 10C8.76 10 9.18 9.82 9.5 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                            <path d="M4.5 4.5C3.2 5.3 2.2 6.5 1.5 8C2.5 10.5 5 12.5 8 12.5C9.2 12.5 10.3 12.1 11.2 11.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                            <path d="M7 3.6C7.3 3.5 7.65 3.5 8 3.5C11 3.5 13.5 5.5 14.5 8C14.2 8.8 13.7 9.5 13.1 10.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                          </svg> :

                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M1.5 8C2.5 5.5 5 3.5 8 3.5C11 3.5 13.5 5.5 14.5 8C13.5 10.5 11 12.5 8 12.5C5 12.5 2.5 10.5 1.5 8Z" stroke="currentColor" strokeWidth="1.2" />
                            <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
                          </svg>
                    }
                      </button>
                    </div>
                  </div>
              }

                {error &&
              <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: '#FEF2F2', borderRadius: '8px' }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6" stroke="#EF4444" strokeWidth="1.2" />
                      <path d="M7 4V7" stroke="#EF4444" strokeWidth="1.2" strokeLinecap="round" />
                      <circle cx="7" cy="10" r="0.5" fill="#EF4444" />
                    </svg>
                    <span className="text-xs text-red-600">{error}</span>
                  </div>
              }

                <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-lg font-medium text-base transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: '#262629',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 500
                }}>
                
                  {loading ?
                <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" strokeDasharray="20" strokeDashoffset="10" />
                      </svg>
                      Sending OTP...
                    </span> :
                mode === 'login' ? 'Sign In with OTP' : 'Create Account'}
                </button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px" style={{ background: '#EDEEF2' }} />
                <span className="text-xs text-[#71717A]">or continue with</span>
                <div className="flex-1 h-px" style={{ background: '#EDEEF2' }} />
              </div>

              {/* Social Auth */}
              <div className="grid grid-cols-2 gap-3">
                {[
              {
                name: 'Google',
                icon:
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M17.64 9.2C17.64 8.57 17.58 7.96 17.47 7.37H9V10.84H13.84C13.63 11.97 12.99 12.92 12.03 13.56V15.82H14.95C16.66 14.25 17.64 11.93 17.64 9.2Z" fill="#4285F4" />
                        <path d="M9 18C11.43 18 13.47 17.19 14.95 15.82L12.03 13.56C11.22 14.1 10.21 14.42 9 14.42C6.66 14.42 4.68 12.84 3.96 10.71H0.96V13.04C2.43 15.97 5.48 18 9 18Z" fill="#34A853" />
                        <path d="M3.96 10.71C3.78 10.17 3.68 9.59 3.68 9C3.68 8.41 3.78 7.83 3.96 7.29V4.96H0.96C0.35 6.17 0 7.55 0 9C0 10.45 0.35 11.83 0.96 13.04L3.96 10.71Z" fill="#FBBC05" />
                        <path d="M9 3.58C10.32 3.58 11.51 4.04 12.44 4.93L15.02 2.35C13.47 0.9 11.43 0 9 0C5.48 0 2.43 2.03 0.96 4.96L3.96 7.29C4.68 5.16 6.66 3.58 9 3.58Z" fill="#EA4335" />
                      </svg>

              },
              {
                name: 'Apple',
                icon:
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M14.5 9.5C14.5 7.5 15.5 6.5 15.5 6.5C14.5 5 13 4.5 12 4.5C10.5 4.5 9.5 5.5 8.5 5.5C7.5 5.5 6.5 4.5 5 4.5C3 4.5 1 6 1 9C1 12 3 16 5 16C6 16 6.5 15.5 8 15.5C9.5 15.5 10 16 11 16C13 16 15 12.5 15.5 11C14.5 10.5 14.5 9.5 14.5 9.5Z" stroke="#262629" strokeWidth="1.2" />
                        <path d="M11 2C11 3.5 10 4.5 9 4.5" stroke="#262629" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>

              }].
              map((provider) =>
              <button
                key={provider.name}
                type="button"
                className="flex items-center justify-center gap-2 h-11 rounded-xl border font-medium text-sm transition-all duration-200 hover:bg-gray-50"
                style={{
                  borderColor: '#EDEEF2',
                  borderRadius: '12px',
                  color: '#262629',
                  background: '#FFFFFF',
                  fontSize: '13px'
                }}>
                
                    {provider.icon}
                    {provider.name}
                  </button>
              )}
              </div>

              <p className="text-center text-xs text-[#71717A] mt-6">
                By continuing, you agree to Rani Kamlapati&apos;s{' '}
                <Link href="#" className="text-[#262629] hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link href="#" className="text-[#262629] hover:underline">Privacy Policy</Link>
              </p>
            </div>)
          }
        </div>
      </div>
    </div>);

}